import React from "react";
import styles from "./styles/music-player.module.scss";
import defaultIcon from "@/assets/convert/music-icon.svg";
import Image from "next/image";
import playIcon from "@/assets/music-player/play.svg";
import playHover from "@/assets/voices/card/play-hover.svg";
import stopPressed from "@/assets/music-player/stop-pressed.svg";
import volumeOn from "@/assets/music-player/volume-on.svg";
import volumeOff from "@/assets/music-player/volume-off.svg";
import IconStateButton from "@/components/pages/home/components/voices/icon-state-button";
import { useMusicPlayer } from "@/providers/music-provider/music-provider";
import { useMobile } from "@/hooks/use-responsive";
import PlayerProgress from "@/components/common/music-player/player-progress";
import VolumeSlider from "@/components/common/music-player/volume-slider";

type MusicPlayerProps = {
  title: string;
  url: string;
  icon?: string;
  format?: string;
  duration?: number;
  stickyProgress?: boolean;
  underImageNode?: React.ReactNode;
};

const MusicPlayer = ({
  title,
  url,
  icon,
  format,
  duration,
  stickyProgress,
  underImageNode,
}: MusicPlayerProps) => {
  const {
    isPlaying,
    playTrack,
    pauseTrack,
    musicPlayer,
    progressRef,
    changeRange,
    calculateTime,
    onDurationChanged,
    onTimeUpdate,
    isMuted,
    changeVolume,
    toggleMute,
    currentTrack,
    volumeProgressRef,
  } = useMusicPlayer();
  const { isMobile } = useMobile();
  const togglePlay = () => {
    if (currentTrackPlaying) {
      pauseTrack();
      return;
    }
    playTrack(url, 0, [url]);
  };

  const currentTrackPlaying = isPlaying && url === currentTrack;
  const shouldDisplayProgress = stickyProgress ?? currentTrackPlaying;

  return (
    <div className={styles.container}>
      <audio
        ref={musicPlayer}
        onDurationChange={onDurationChanged}
        onTimeUpdate={onTimeUpdate}
      />
      <div className={styles.header}>
        <div className={styles.icon_wrapper}>
          <Image
            className={styles.icon}
            src={icon || defaultIcon}
            alt="music-icon"
            sizes="auto"
            fill
          />
        </div>
        <div className={styles.title_container}>
          <div className={styles.title}>{title}</div>
          {format && (
            <div className={styles.path}>
              {title}.{format}
            </div>
          )}
        </div>
        {underImageNode}
      </div>
      {shouldDisplayProgress && (
        <PlayerProgress
          calculateTime={calculateTime}
          musicPlayer={musicPlayer}
          progressRef={progressRef}
          onChange={changeRange}
          duration={duration}
        />
      )}
      {isMobile && (
        <div className={styles.play_icon}>
          <IconStateButton
            baseIcon={playIcon}
            hoverIcon={playHover}
            activeIcon={currentTrackPlaying ? stopPressed : playIcon}
            onClick={togglePlay}
            size={40}
            isActive={isPlaying}
          />
        </div>
      )}
      {!isMobile && (
        <div className={styles.play_wrapper}>
          <VolumeSlider
            changeVolume={changeVolume}
            isMuted={isMuted}
            toggleMute={toggleMute}
            volumeProgressRef={volumeProgressRef}
          />
          <div className={styles.play_icon}>
            <IconStateButton
              baseIcon={playIcon}
              hoverIcon={playHover}
              activeIcon={currentTrackPlaying ? stopPressed : playIcon}
              onClick={togglePlay}
              size={50}
              isActive={isPlaying}
            />
          </div>
          <div className={styles.empty_spacer} />
        </div>
      )}
    </div>
  );
};

export default MusicPlayer;
